import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { setProfile } from './profileSlice';
import { api } from '../../services/api';
import { socket } from '../../services/socket';
import { InfluencerProfile, AdvertiserProfile } from '../types';

export interface User {
  _id: string;
  email: string;
  username: string;
  role: 'influencer' | 'advertiser';
  profileImage?: string;
  isVerified?: boolean;
  influencerProfile?: InfluencerProfile;
  advertiserProfile?: AdvertiserProfile;
}

export const defaultInfluencerProfile: InfluencerProfile = {
  bio: '',
  socialMediaProfiles: [],
  totalFollowers: 0,
  averageEngagementRate: 0,
  categories: [],
  totalCampaigns: 0,
  rating: 0,
  totalRatings: 0,
  location: '',
  languages: [],
  preferredBudget: { amount: 0, currency: 'USD' },
  availability: true
};

export const defaultAdvertiserProfile: AdvertiserProfile = {
  bio: '',
  companyName: '',
  companyWebsite: '',
  industry: [],
  totalCampaigns: 0,
  rating: 0,
  totalRatings: 0,
  location: '',
  contactEmail: '',
  contactPhone: ''
};

export interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
  isLoading: false,
};

// Helper functions for token management
const updateTokenInStorage = (token: string | null) => {
  if (token) {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Initialize api headers if token exists
if (initialState.token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${initialState.token}`;
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { dispatch }) => {
    const response = await api.post('/api/auth/login', credentials);
    const { token, user } = response.data;

    // Set token in localStorage and API headers
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Connect socket
    socket.connect();

    // Set profile in Redux store
    dispatch(setProfile({
      userType: user.role,
      name: user.username,
      email: user.email,
      isAuthenticated: true,
      avatar: user.profileImage || null,
      influencerProfile: user.role === 'influencer' ? {
        ...defaultInfluencerProfile,
        ...user.influencerProfile
      } : undefined,
      advertiserProfile: user.role === 'advertiser' ? {
        ...defaultAdvertiserProfile,
        ...user.advertiserProfile
      } : undefined
    }));

    return { token, user };
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: { email: string; password: string; username: string; role: 'influencer' | 'advertiser' }, { dispatch }) => {
    const response = await api.post('/api/auth/register', userData);
    const { token, user } = response.data;

    // Set token in localStorage and API headers
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Connect socket
    socket.connect();

    // Set profile in Redux store
    dispatch(setProfile({
      userType: user.role,
      name: user.username,
      email: user.email,
      isAuthenticated: true,
      avatar: user.profileImage || null,
      influencerProfile: user.role === 'influencer' ? {
        ...defaultInfluencerProfile,
        ...user.influencerProfile
      } : undefined,
      advertiserProfile: user.role === 'advertiser' ? {
        ...defaultAdvertiserProfile,
        ...user.advertiserProfile
      } : undefined
    }));

    return { token, user };
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    // Clear token from localStorage
    localStorage.removeItem('token');
    
    // Clear API authorization header
    delete api.defaults.headers.common['Authorization'];
    
    // Disconnect socket
    socket.disconnect();
    
    // Clear Redux state
    dispatch(setProfile({
      userType: null,
      name: null,
      email: null,
      isAuthenticated: false,
      avatar: null,
      influencerProfile: undefined,
      advertiserProfile: undefined
    }));
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/auth/profile', profileData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update profile');
    }
  }
);

// Add verifyToken thunk
export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { dispatch }) => {
    console.log('verifyToken thunk started');
    const token = localStorage.getItem('token');
    console.log('Token from localStorage:', { hasToken: !!token });
    
    if (!token) {
      console.log('No token found in localStorage');
      throw new Error('No token found');
    }

    try {
      console.log('Making /api/auth/me request');
      const response = await api.get('/api/auth/me');
      const { data: rawUser } = response.data;
      
      // Normalize user data to handle both id and _id
      const user = {
        ...rawUser,
        _id: rawUser._id || rawUser.id, // Handle both id formats
        id: undefined // Remove the id field to avoid confusion
      };

      if (!user || !user._id) {
        console.error('Invalid user data received:', { rawUser, normalizedUser: user });
        throw new Error('Invalid user data');
      }

      console.log('User data received:', { 
        id: user._id,
        role: user.role,
        email: user.email,
        hasInfluencerProfile: !!user.influencerProfile,
        hasAdvertiserProfile: !!user.advertiserProfile,
        rawUser // Log the full user object for debugging
      });

      // Set auth state first
      console.log('Setting auth state in Redux store');
      const authData = { user, token };
      
      // Then set profile state
      console.log('Setting profile in Redux store');
      await dispatch(setProfile({
        userType: user.role,
        name: user.username,
        email: user.email,
        isAuthenticated: true,
        avatar: user.profileImage || null,
        influencerProfile: user.role === 'influencer' ? {
          ...defaultInfluencerProfile,
          ...user.influencerProfile
        } : undefined,
        advertiserProfile: user.role === 'advertiser' ? {
          ...defaultAdvertiserProfile,
          ...user.advertiserProfile
        } : undefined
      })).unwrap();

      console.log('Auth and profile state set in Redux store:', {
        userId: user._id,
        role: user.role,
        hasToken: !!token,
        hasProfile: true
      });

      return authData;
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear invalid token
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      socket.disconnect();
      throw error;
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      console.log('Setting user in auth state:', {
        userId: action.payload._id,
        role: action.payload.role
      });
      state.user = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Verify Token
      .addCase(verifyToken.pending, (state) => {
        console.log('verifyToken.pending - setting loading state');
        state.loading = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        console.log('verifyToken.fulfilled - updating state with user data:', {
          userId: action.payload.user._id,
          role: action.payload.user.role,
          hasToken: !!action.payload.token,
          rawUser: action.payload.user // Log the full user object
        });
        state.loading = false;
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        console.error('verifyToken.rejected - clearing state:', action.payload);
        state.loading = false;
        state.isLoading = false;
        state.error = action.payload as string;
        state.user = null;
        state.token = null;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });
  },
});

export const { clearError, setUser, setToken } = authSlice.actions;

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

export default authSlice.reducer; 